import { flow, isEmpty, keys, set } from 'lodash/fp';

import { StoreListing, useStoreSettings } from '@portals/api/partners';
import { useWizard } from '@portals/framework';
import { useOpenModal } from '@portals/redux';
import { PeriodEnum, ProductPricingModel } from '@portals/types';

import { StepId, StoreListingWizardContext } from './types';
import { AddRequiredStoreSettingsModalData } from '../publish-product-missing-requirements/AddRequiredStoreSettings';

export function useStoreListingWizard() {
  return useWizard<StoreListingWizardContext, StepId>();
}

export function useValidateBeforePublish() {
  const openModal = useOpenModal();
  const storeSettings = useStoreSettings();
  const { contextData, setContextData } = useStoreListingWizard();

  const validatePaymentSettings = () => {
    if (
      contextData.selectedProduct?.pricing_model ===
      ProductPricingModel.Personalized
    ) {
      return true;
    }

    const { paymentSettings } = contextData;

    if (isEmpty(paymentSettings)) {
      setContextData((prev) => ({
        ...prev,
        paymentSettingsGeneralError: 'Please add at least one price',
      }));

      return false;
    }

    const newPaymentSettingsAccordionValuesSet = new Set<PeriodEnum>();
    const newPaymentSettings: StoreListingWizardContext['paymentSettings'] =
      contextData.paymentSettings;

    (keys(paymentSettings) as PeriodEnum[]).forEach((period) => {
      const prices = paymentSettings[period]?.prices.map((price) => ({
        ...price,
        error: price.amountInScu === null,
      }));

      if (prices?.some((price) => price.error)) {
        newPaymentSettingsAccordionValuesSet.add(period);
      }

      newPaymentSettings[period] = {
        ...newPaymentSettings[period],
        prices,
      };
    });

    setContextData((prev) =>
      flow([
        set('paymentSettings', newPaymentSettings),
        set(
          'paymentSettingsAccordionValues',
          Array.from(newPaymentSettingsAccordionValuesSet)
        ),
      ])(prev)
    );

    return newPaymentSettingsAccordionValuesSet.size === 0;
  };

  const validatePublishPrerequisites = ({
    storeListing,
    onFulfillment,
  }: {
    storeListing: StoreListing;
    onFulfillment: () => void;
  }) => {
    const isMissingSettings =
      !storeSettings.data?.privacy_policy ||
      !storeSettings.data?.terms_of_service ||
      !storeSettings.data?.cancellations_and_returns_policy ||
      !storeSettings.data?.address_1;

    const isUnpublishedModel =
      storeListing.product.device_model &&
      !storeListing.product.device_model.active;

    if (!isMissingSettings && !isUnpublishedModel) {
      onFulfillment();
      return;
    }

    const unpublishedDeviceModelsIds =
      storeListing.product.device_model &&
      !storeListing.product.device_model.active
        ? [storeListing.product.device_model.id]
        : undefined;

    if (isMissingSettings || isUnpublishedModel) {
      openModal<AddRequiredStoreSettingsModalData>('AddRequiredStoreSettings', {
        unpublishedDeviceModelsIds,
        onFulfillment,
      });
    }
  };

  return { validatePaymentSettings, validatePublishPrerequisites };
}
